import {
  IDIncomeVerificationStatus,
  NovaEnvironment,
  NovaVerificationStatus,
  VerificationType,
} from '@common/entities';
import { get, post } from 'config/api/config';

const VERIFICATION_ENDPOINT = '/viewer/verifications';

export interface EligibilityCompassStatusResponse {
  isEnabled: boolean;
  errors: string[];
}

export const getEligibilityCompassStatus = async (
  responseId?: string,
): Promise<EligibilityCompassStatusResponse> => {
  const response = await get<EligibilityCompassStatusResponse>(
    `${VERIFICATION_ENDPOINT}/eligibility-compass/enabled`,
    responseId ? { params: { responseId } } : undefined,
  );
  return response?.data;
};

export interface EligibilityCompassVerification {
  id?: number;
  cid?: number;
  applicant_id?: number;
  application_id?: number;
  provider?: string | null;
  description?: string | null;
  consumer_id?: string | null;
  status?: NovaVerificationStatus | null;
  updated_by?: number | null;
  updated_on?: string | null;
  created_by?: number | null;
  created_on?: string | null;
  approved_on?: string | null;
  rejected_on?: string | null;
  is_token_expired?: boolean;
  token?: string;
}

export const getEligibilityCompassVerification = async (
  responseId?: string,
): Promise<EligibilityCompassVerification[]> => {
  const responsePath = responseId ? `/${encodeURIComponent(responseId)}` : '';
  const response = await get<EligibilityCompassVerification[]>(
    `${VERIFICATION_ENDPOINT}/income/eligibility-compass/verification${responsePath}`,
  );
  return response?.data ?? [];
};

interface GetVerificationLinkTokenResponse {
  linkToken: string;
}

export const getVerificationLinkToken = async (type: VerificationType, email: string) => {
  if (!type) throw new Error('Type is required');
  if (!email) throw new Error('Email is required');

  const response = await get<GetVerificationLinkTokenResponse>(
    `${VERIFICATION_ENDPOINT}/${type}/plaid/link-token`,
    { params: { email } },
  );
  return response?.data;
};

/**
 * NOVA Eligibility Compass initialization token returned by the backend
 * (proxied from ResidentVerify, which calls NOVA's `/connect/initialization`).
 *
 * `token`, `publicKey` (NOVA's `publicId`), `productId` and `env` are what
 * NovaConnect needs to launch; `publicToken` identifies the resulting report for
 * webhook correlation and server-side retrieval.
 */
export interface EligibilityCompassTokenResponse {
  token: string;
  publicToken: string;
  consumerId: string;
  productId: string;
  publicKey: string;
  createdOn: string;
  env: NovaEnvironment;
}

/**
 * Requests an Eligibility Compass token for the current applicant. The applicant
 * context and the downstream ResidentVerify URL/params are resolved server-side
 * from the encrypted viewer session, so nothing sensitive is sent from here.
 */
export const getEligibilityCompassToken = async (
  responseId?: string,
): Promise<EligibilityCompassTokenResponse> => {
  const responsePath = responseId ? `/${encodeURIComponent(responseId)}` : '';
  const response = await get<EligibilityCompassTokenResponse>(
    `${VERIFICATION_ENDPOINT}/income/eligibility-compass/token${responsePath}`,
  );
  return response?.data;
};

export interface SaveEligibilityCompassVerificationRequest {
  status: NovaVerificationStatus;
  token?: string;
  publicToken?: string;
  consumerId?: string;
  productId?: string;
  publicKey?: string;
  description?: string;
}

export const saveEligibilityCompassVerification = async (
  request: SaveEligibilityCompassVerificationRequest,
  responseId?: string,
) => {
  const responsePath = responseId ? `?responseId=${encodeURIComponent(responseId)}` : '';
  const response = await post(
    `${VERIFICATION_ENDPOINT}/income/eligibility-compass/verification${responsePath}`,
    request,
  );
  return response?.data;
};

export const sendVerificationText = async (isResend = false, email?: string) => {
  if (!email) throw new Error('Email is required');

  const response = await post(
    `${VERIFICATION_ENDPOINT}/identity/send-text?isResend=${isResend}&email=${encodeURIComponent(email)}`,
    {},
  );
  return response?.data;
};

interface SetVerificationStatusResponse {
  success: boolean;
  message: string;
}
export const setVerificationStatus = async (
  type: VerificationType,
): Promise<SetVerificationStatusResponse> => {
  if (!type) throw new Error('Verification type is required');

  const response = await post(`${VERIFICATION_ENDPOINT}/${type}/status`);
  return response?.data;
};

interface IsVerificationEnabledResponse {
  clientId: number;
  entrataApplicationId: number;
  propertyId: number;
  isIdVerificationEnabled: boolean;
  isIncomeVerificationEnabled: boolean;
}
export const getIsVerificationEnabled = async (): Promise<IsVerificationEnabledResponse> => {
  const response = await get(`${VERIFICATION_ENDPOINT}/enabled`);
  return response?.data;
};

export interface VerificationStatusResponse {
  entrataApplicationId: number;
  applicantId: number;
  idVerification: {
    status: IDIncomeVerificationStatus | null;
    verificationType: VerificationType.IdentityVerification;
  };
  incomeVerification: {
    status: IDIncomeVerificationStatus | null;
    verificationType: VerificationType | null;
  };
}
export const getVerificationStatus = async (
  signal?: AbortSignal,
): Promise<VerificationStatusResponse> => {
  const response = await get(`${VERIFICATION_ENDPOINT}/status`, {
    signal,
  });
  return response?.data;
};

export const getVerificationStatusLP = async (
  responseId: string,
): Promise<VerificationStatusResponse> => {
  const response = await get(
    `${VERIFICATION_ENDPOINT}/lp/status/${encodeURIComponent(responseId)}`,
  );
  return response?.data;
};

export const updateIncomeVerificationType = async (
  type: VerificationType,
  email: string,
): Promise<SetVerificationStatusResponse> => {
  if (!type) throw new Error('Verification type is required');
  if (!email) throw new Error('Email is required');

  const response = await post(
    `${VERIFICATION_ENDPOINT}/income/update-verification-type/${type}?email=${encodeURIComponent(email)}`,
  );
  return response?.data;
};

export interface ReInitiateVerificationResponse {
  success: boolean;
  message: string;
}
export const reInitiateVerification = async (
  email: string,
): Promise<ReInitiateVerificationResponse> => {
  if (!email) throw new Error('Email is required');

  const response = await post<ReInitiateVerificationResponse>(
    `${VERIFICATION_ENDPOINT}/reinitiate?email=${encodeURIComponent(email)}`,
  );
  return response?.data;
};
